import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError, formatINR } from "@/lib/format";
import DropUpload from "@/components/DropUpload";
import DownloadPlayer from "@/components/DownloadPlayer";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Tv,
  FolderOpen,
  ListVideo,
  CalendarClock,
  Plus,
  Copy,
  ExternalLink,
  Trash2,
  Monitor,
  PlayCircle,
  Sparkles,
  BarChart3,
  Activity,
  Film,
  Wifi,
  QrCode,
  Download,
  Radio,
  FileText,
  Smartphone,
  Check,
  ScanLine,
  CameraOff,
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SignageDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["screens", "media", "playlists", "schedules", "analytics"];
  const initialTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "screens";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (VALID_TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = async () => {
    const { data } = await api.get("/signage/me");
    setData(data);
  };
  useEffect(() => {
    load();
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      await payWithRazorpay({
        purpose: "signage_subscription",
        amount_inr: 400,
        user,
        description: "Signage Basic · ₹400/mo",
        onSuccess: load,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="text-zinc-500">Loading signage…</div>;

  if (!data.subscription || data.subscription.status !== "active") {
    return (
      <div className="space-y-8" data-testid="signage-upsell">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Signage
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Digital signage, simplified.
          </h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Manage every screen in your cafe, showroom or classroom. Upload
            media, build playlists, schedule by day and hour. Any screen with a
            browser becomes a Mirrorloops-powered display.
          </p>
        </div>
        <div className="rounded-3xl border border-[#D4B878]/40 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c] p-10 max-w-xl ml-glow">
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Signage Basic
          </div>
          <div className="text-6xl font-black">
            ₹400<span className="text-xl text-zinc-500 font-semibold">/mo</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-zinc-300">
            <li className="flex gap-2"><Sparkles size={14} className="text-[#E8D6A3] mt-1" /> Unlimited screens</li>
            <li className="flex gap-2"><Sparkles size={14} className="text-[#E8D6A3] mt-1" /> 50-item media library</li>
            <li className="flex gap-2"><Sparkles size={14} className="text-[#E8D6A3] mt-1" /> Day/hour scheduling</li>
            <li className="flex gap-2"><Sparkles size={14} className="text-[#E8D6A3] mt-1" /> Browser-based player · no extra hardware</li>
          </ul>
          <Button
            onClick={subscribe}
            disabled={busy}
            data-testid="signage-subscribe-btn"
            className="mt-8 w-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full py-6 font-bold"
          >
            {busy ? "Opening checkout…" : "Start · ₹400/mo"}
          </Button>
          <div className="text-[11px] text-zinc-500 mt-3 text-center">
            Billed monthly via Razorpay · cancel anytime. TEST-MODE-MOCKED in this build.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="signage-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Signage · Active
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Your screens.
          </h1>
          <p className="text-zinc-400 mt-2">
            Plan expires{" "}
            {new Date(data.subscription.expires_at).toLocaleDateString()} ·{" "}
            {data.counts.screens} screens · {data.counts.media} media ·{" "}
            {data.counts.playlists} playlists
          </p>
        </div>
        <DownloadPlayer
          variant="ghost"
          testid="signage-dashboard-download-player"
          label="Download Player"
        />
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          setSearchParams({ tab: v });
        }}
      >
        <TabsList className="bg-white/5 border border-white/10 rounded-full p-1">
          <TabsTrigger value="screens" data-testid="sig-tab-screens" className="rounded-full">
            <Tv size={14} className="mr-2" /> Screens
          </TabsTrigger>
          <TabsTrigger value="media" data-testid="sig-tab-media" className="rounded-full">
            <FolderOpen size={14} className="mr-2" /> Media
          </TabsTrigger>
          <TabsTrigger value="playlists" data-testid="sig-tab-playlists" className="rounded-full">
            <ListVideo size={14} className="mr-2" /> Playlists
          </TabsTrigger>
          <TabsTrigger value="schedules" data-testid="sig-tab-schedules" className="rounded-full">
            <CalendarClock size={14} className="mr-2" /> Schedules
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="sig-tab-analytics" className="rounded-full">
            <BarChart3 size={14} className="mr-2" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="screens" className="mt-8">
          <ScreensTab onChanged={load} />
        </TabsContent>
        <TabsContent value="media" className="mt-8">
          <MediaTab onChanged={load} />
        </TabsContent>
        <TabsContent value="playlists" className="mt-8">
          <PlaylistsTab onChanged={load} />
        </TabsContent>
        <TabsContent value="schedules" className="mt-8">
          <SchedulesTab onChanged={load} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-8">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- SCREENS ---------- */
/**
 * BindTVButton — inline dialog that lets the venue owner claim a pair
 * code that a Mirrorloops native shell (LG IPK / Sony APK) is
 * displaying on-screen. Calls POST /signage/pair-open/{code}/bind and
 * closes on success, prompting the parent to reload the screens list.
 *
 * Flow:
 *   1. TV shows "PAIR CODE: ABC123".
 *   2. Owner opens Screens → Bind a TV → types code + optional name.
 *   3. On submit, backend creates a signage_screens row + returns a
 *      play_token; the TV's poll picks it up 2s later and switches
 *      into /play/{token}.
 */
function BindTVButton({ onBound }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null); // pre-flight peek {model, source, bound}
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState("");
  const scannerRef = useRef(null);

  const trimmedCode = code.trim().toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 12);

  // Best-effort pre-flight: peek at the pair code as the owner types the
  // 6th character so we can show the model + a "TV is waiting" indicator.
  useEffect(() => {
    if (trimmedCode.length < 5) { setInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/signage/pair-open/${trimmedCode}`);
        if (!cancelled) setInfo(r.data);
      } catch { if (!cancelled) setInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [trimmedCode]);

  // Extract a pair code from any of the payload shapes we might scan off
  // a TV: bare code ("ABC234"), deep-link ("mirrorloops://pair/ABC234"),
  // or the marketing pair URL ("https://mirrorloops.com/pair/ABC234").
  const extractCode = (raw) => {
    if (!raw) return "";
    const s = raw.trim();
    const m = s.match(/pair\/([A-Za-z0-9]+)/i);
    if (m) return m[1].toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 12);
    // Fall back to alphanumeric filter — handles bare codes with padding.
    return s.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 12);
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch { /* ignore — scanner may already be torn down */ }
    scannerRef.current = null;
    setScanning(false);
  };

  const startScanner = async () => {
    setScanErr("");
    setScanning(true);
    // Defer to next tick so the #ml-pair-qr-reader div is mounted before
    // Html5Qrcode tries to attach to it.
    setTimeout(async () => {
      try {
        const qr = new Html5Qrcode("ml-pair-qr-reader");
        scannerRef.current = qr;
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          async (decoded) => {
            const parsed = extractCode(decoded);
            if (parsed && parsed.length >= 5) {
              setCode(parsed);
              await stopScanner();
              toast.success(`Pair code detected: ${parsed}`);
            }
          },
          () => { /* per-frame decode fails are noise, ignore */ }
        );
      } catch (e) {
        setScanErr(
          e?.message?.includes("Permission")
            ? "Camera permission denied — allow camera access and retry."
            : e?.message || "Camera unavailable — enter the code manually."
        );
        setScanning(false);
      }
    }, 50);
  };

  // Clean up scanner if the dialog is closed mid-scan.
  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setCode(""); setName(""); setInfo(null); setScanErr("");
      stopScanner();
    }
  };

  const submit = async () => {
    if (trimmedCode.length < 5) return;
    setBusy(true);
    try {
      const r = await api.post(`/signage/pair-open/${trimmedCode}/bind`, {
        screen_name: name.trim() || "New signage screen",
      });
      toast.success(`Screen "${r.data.screen_name}" bound — TV switches in ~2s`);
      onOpenChange(false);
      onBound?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="sig-bind-tv-btn"
          className="border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] hover:bg-[#CCFF00]/[0.12] text-[#CCFF00] rounded-full"
        >
          <Smartphone size={14} className="mr-2" /> Bind a TV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone size={18} className="text-[#CCFF00]" /> Bind a TV to
            your account
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-white/60 leading-relaxed">
            On the TV, open the Mirrorloops Signage app — it'll show a
            6-character pair code full-screen. Type it here, or point your
            phone camera at the code to auto-fill it.
          </p>

          {/* Mode toggle: Type / Scan */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { if (scanning) stopScanner(); }}
              data-testid="bind-tv-mode-type-btn"
              className={`flex-1 rounded-full text-xs h-8 ${
                !scanning
                  ? "border-[#CCFF00]/40 bg-[#CCFF00]/[0.08] text-[#CCFF00]"
                  : "border-white/10 bg-white/[0.02] text-white/60"
              }`}
            >
              Type code
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { if (!scanning) startScanner(); }}
              data-testid="bind-tv-mode-scan-btn"
              className={`flex-1 rounded-full text-xs h-8 ${
                scanning
                  ? "border-[#CCFF00]/40 bg-[#CCFF00]/[0.08] text-[#CCFF00]"
                  : "border-white/10 bg-white/[0.02] text-white/60"
              }`}
            >
              <ScanLine size={12} className="mr-1" /> Scan QR
            </Button>
          </div>

          {scanning ? (
            <div className="space-y-2">
              <div
                id="ml-pair-qr-reader"
                data-testid="bind-tv-qr-reader"
                className="rounded-md overflow-hidden bg-black w-full aspect-video min-h-[220px]"
              />
              <div className="flex items-center justify-between text-[10px] text-white/50">
                <span>Point at the QR shown on the TV screen.</span>
                <button
                  onClick={stopScanner}
                  data-testid="bind-tv-qr-stop-btn"
                  className="flex items-center gap-1 text-white/70 hover:text-white"
                >
                  <CameraOff size={11} /> Stop
                </button>
              </div>
              {scanErr && (
                <div
                  data-testid="bind-tv-qr-error"
                  className="p-2 rounded border border-red-400/30 bg-red-500/[0.06] text-red-300 text-[11px]"
                >
                  {scanErr}
                </div>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                Pair code
              </Label>
              <Input
                data-testid="sig-bind-tv-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. ABC234"
                maxLength={12}
                autoFocus
                className="bg-white/5 border-white/10 mt-1 font-mono text-2xl tracking-[0.24em] text-center uppercase"
              />
            </div>
          )}
          {info && (
            <div
              data-testid="sig-bind-tv-preflight"
              className={`p-2 border rounded flex items-center gap-2 text-[11px] ${
                info.bound
                  ? "border-red-400/30 bg-red-500/[0.06] text-red-300"
                  : "border-[#CCFF00]/30 bg-[#CCFF00]/[0.06] text-[#CCFF00]"
              }`}
            >
              {info.bound ? (
                <>
                  <Trash2 size={11} /> That code is already bound to another
                  screen.
                </>
              ) : (
                <>
                  <Check size={11} /> {info.model || "TV"} is waiting to be
                  paired ({info.source || "unknown app"}).
                </>
              )}
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              Screen name (optional)
            </Label>
            <Input
              data-testid="sig-bind-tv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cafe Window · 55NU870"
              className="bg-white/5 border-white/10 mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || trimmedCode.length < 5 || info?.bound}
            data-testid="sig-bind-tv-submit"
            className="bg-[#CCFF00] hover:bg-[#E8FF66] text-black rounded-full disabled:opacity-40"
          >
            {busy ? "Binding…" : "Bind TV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ScreensTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", orientation: "landscape" });
  const [shareScreen, setShareScreen] = useState(null);

  const load = async () => {
    const { data } = await api.get("/signage/screens");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/signage/screens", form);
      toast.success("Screen added");
      setOpen(false);
      setForm({ name: "", location: "", orientation: "landscape" });
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this screen?")) return;
    await api.delete(`/signage/screens/${id}`);
    toast.success("Removed"); load(); onChanged();
  };

  const playUrl = (tok) =>
    `${window.location.origin}/play/${tok}`;

  return (
    <div className="space-y-5" data-testid="sig-screens">
      <div className="flex justify-end gap-2">
        <BindTVButton onBound={() => { load(); onChanged(); }} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sig-add-screen-btn" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">
              <Plus size={16} className="mr-2" /> Add screen
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-md">
            <DialogHeader><DialogTitle>Add a screen</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name">
                <Input data-testid="sig-screen-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Location">
                <Input data-testid="sig-screen-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Orientation">
                <Select value={form.orientation} onValueChange={(v) => setForm({ ...form, orientation: v })}>
                  <SelectTrigger data-testid="sig-screen-orientation" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">Landscape</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button disabled={!form.name} onClick={create} data-testid="sig-screen-create" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && (
          <div className="col-span-full rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
            No screens yet.
          </div>
        )}
        {list.map((s) => (
          <div key={s.id} data-testid={`sig-screen-${s.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="p-2 rounded-lg bg-[#D4B878]/10 text-[#E8D6A3]">
                <Monitor size={18} />
              </div>
              <Badge variant="outline" className={`capitalize ${s.status === "online" ? "border-emerald-500/40 text-emerald-400" : "border-white/10 text-zinc-400"}`}>
                {s.status}
              </Badge>
            </div>
            <div className="font-bold text-lg">{s.name}</div>
            <div className="text-xs text-zinc-500">{s.location || "—"} · {s.orientation}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-4">Play URL</div>
            <div className="flex items-center gap-1 mt-1 bg-black/40 rounded-lg px-2 py-1.5 border border-white/5">
              <span className="text-xs text-zinc-300 truncate flex-1">{playUrl(s.play_token)}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { navigator.clipboard?.writeText(playUrl(s.play_token)); toast.success("Copied"); }}
                data-testid={`sig-screen-copy-${s.id}`}
                className="h-6 w-6 p-0"
              ><Copy size={12} /></Button>
              <a href={playUrl(s.play_token)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" data-testid={`sig-screen-open-${s.id}`}>
                  <ExternalLink size={12} />
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                onClick={() => setShareScreen(s)}
                data-testid={`sig-screen-share-${s.id}`}
                size="sm"
                className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full h-8 px-3"
              >
                <QrCode size={12} className="mr-1.5" /> Share QR
              </Button>
              <Button
                onClick={() => remove(s.id)}
                data-testid={`sig-screen-del-${s.id}`}
                variant="ghost"
                size="sm"
                className="text-zinc-500 hover:text-red-400 px-2 h-8"
              ><Trash2 size={12} className="mr-1" /> Remove</Button>
            </div>
          </div>
        ))}
      </div>

      <ShareScreenDialog
        screen={shareScreen}
        onClose={() => setShareScreen(null)}
        playUrl={(tok) => `${window.location.origin}/play/${tok}`}
      />
    </div>
  );
}

/* ---------- SHARE SCREEN QR DIALOG (Share + Pair tabs) ---------- */
function ShareScreenDialog({ screen, onClose, playUrl }) {
  const shareQrRef = useRef(null);
  const pairQrRef = useRef(null);
  const [tab, setTab] = useState("share");
  const [pair, setPair] = useState(null);
  const [pairBusy, setPairBusy] = useState(false);

  const open = Boolean(screen);
  const shareUrl = screen ? playUrl(screen.play_token) : "";
  const remoteUrl = pair ? `${window.location.origin}/remote/${pair.code}` : "";

  useEffect(() => {
    // Reset when the dialog closes or screen changes
    if (!screen) {
      setPair(null);
      setTab("share");
    }
  }, [screen]);

  const generatePair = async () => {
    if (!screen) return;
    setPairBusy(true);
    try {
      const { data } = await api.post(`/signage/screens/${screen.id}/pair-code`);
      setPair(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPairBusy(false);
    }
  };

  const downloadPng = (kind) => {
    const node = kind === "pair" ? pairQrRef.current : shareQrRef.current;
    const svg = node?.querySelector("svg");
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const blob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
    const url64 = URL.createObjectURL(blob);
    const header = kind === "pair" ? "Mirrorloops · Pair & Control" : "Mirrorloops Signage";
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0908";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 96, 96, 832, 832);
      ctx.fillStyle = "#E8D6A3";
      ctx.font = "bold 42px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(header, size / 2, 72);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 36px sans-serif";
      ctx.fillText(screen.name, size / 2, size - 40);
      const a = document.createElement("a");
      a.download = `mirrorloops-${kind}-${screen.name.replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      URL.revokeObjectURL(url64);
    };
    img.src = url64;
  };

  const copy = (val, label = "URL") => {
    navigator.clipboard?.writeText(val);
    toast.success(`${label} copied`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="sig-share-dialog"
        className="bg-[#14110E] border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={18} className="text-[#E8D6A3]" /> Share this screen
          </DialogTitle>
        </DialogHeader>

        {screen && (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-white/5 border border-white/10 rounded-full p-1 w-full">
              <TabsTrigger
                value="share"
                data-testid="sig-share-tab-share"
                className="rounded-full flex-1"
              >
                <PlayCircle size={14} className="mr-2" /> Open player
              </TabsTrigger>
              <TabsTrigger
                value="pair"
                data-testid="sig-share-tab-pair"
                className="rounded-full flex-1"
              >
                <Radio size={14} className="mr-2" /> Pair & control
              </TabsTrigger>
            </TabsList>

            <TabsContent value="share" className="mt-5 space-y-4">
              <div className="text-xs text-zinc-400">
                Stick this QR on the wall. Staff scans → the player opens on their tablet —
                no login, no typing.
              </div>
              <div
                ref={shareQrRef}
                className="rounded-2xl bg-white p-6 flex items-center justify-center"
                data-testid="sig-share-qr"
              >
                <QRCodeSVG
                  value={shareUrl}
                  size={240}
                  level="H"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#0A0908"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                  {screen.name} · {screen.orientation}
                </div>
                <div className="flex items-center gap-1 bg-black/40 rounded-lg px-2 py-1.5 border border-white/10">
                  <span className="text-xs text-zinc-300 truncate flex-1">{shareUrl}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copy(shareUrl, "Play URL")}
                    data-testid="sig-share-copy"
                    className="h-6 w-6 p-0"
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => downloadPng("share")}
                  data-testid="sig-share-download"
                  className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full w-full"
                >
                  <Download size={14} className="mr-2" /> QR PNG
                </Button>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/signage/screens/${screen.id}/setup-guide.pdf?token=${encodeURIComponent(localStorage.getItem("mirrorloops_token") || "")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sig-share-setup-pdf"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-white/80 hover:text-white rounded-full h-10 text-sm font-semibold transition"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/signage/screens/${screen.id}/setup-guide.pdf`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("mirrorloops_token") || ""}` },
                      });
                      if (!r.ok) throw new Error("Failed to load");
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `mirrorloops-${(screen.name || "screen").replace(/\s+/g, "-").toLowerCase()}-setup.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.error("Could not download PDF");
                    }
                  }}
                >
                  <FileText size={14} /> Setup PDF
                </a>
              </div>
            </TabsContent>

            <TabsContent value="pair" className="mt-5 space-y-4">
              {!pair && (
                <div className="space-y-3">
                  <div className="text-xs text-zinc-400">
                    Generate a 30-minute <span className="text-[#E8D6A3] font-semibold">pair code</span>.
                    Any phone that scans it gets a mini remote — Next / Previous / Mute / Reload —
                    that sends commands directly to this screen.
                  </div>
                  <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center">
                    <div className="inline-flex p-3 rounded-xl bg-[#D4B878]/10 text-[#E8D6A3] mb-3">
                      <Radio size={24} />
                    </div>
                    <div className="text-sm text-zinc-400 mb-4">
                      No active remote session for this screen.
                    </div>
                    <Button
                      onClick={generatePair}
                      disabled={pairBusy}
                      data-testid="sig-pair-generate"
                      className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
                    >
                      {pairBusy ? "Generating…" : "Generate pair QR"}
                    </Button>
                  </div>
                </div>
              )}

              {pair && (
                <>
                  <div className="text-xs text-zinc-400">
                    Staff scans this with their phone camera — it opens a remote that can
                    skip clips, mute, and reload this screen live.
                  </div>
                  <div
                    ref={pairQrRef}
                    className="rounded-2xl bg-white p-6 flex items-center justify-center"
                    data-testid="sig-pair-qr"
                  >
                    <QRCodeSVG
                      value={remoteUrl}
                      size={240}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#0A0908"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                      Expires {new Date(pair.expires_at).toLocaleTimeString()} · {pair.ttl_minutes} min session
                    </div>
                    <div className="flex items-center gap-1 bg-black/40 rounded-lg px-2 py-1.5 border border-white/10">
                      <span className="text-xs text-zinc-300 truncate flex-1">{remoteUrl}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copy(remoteUrl, "Remote URL")}
                        data-testid="sig-pair-copy"
                        className="h-6 w-6 p-0"
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => downloadPng("pair")}
                      data-testid="sig-pair-download"
                      className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full flex-1"
                    >
                      <Download size={14} className="mr-2" /> Download PNG
                    </Button>
                    <Button
                      onClick={generatePair}
                      disabled={pairBusy}
                      data-testid="sig-pair-regenerate"
                      variant="outline"
                      className="bg-white/5 border-white/10 rounded-full"
                    >
                      New code
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- MEDIA ---------- */
function MediaTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", type: "image" });

  const load = async () => { const { data } = await api.get("/signage/media"); setList(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/signage/media", form);
      toast.success("Media added");
      setOpen(false);
      setForm({ name: "", url: "", type: "image" });
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/signage/media/${id}`);
    toast.success("Removed"); load(); onChanged();
  };

  return (
    <div className="space-y-5" data-testid="sig-media">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sig-add-media-btn" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">
              <Plus size={16} className="mr-2" /> Add media
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle>Add to media library</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Name">
                <Input data-testid="sig-media-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" />
              </Field>
              <Field label="Drop image or video">
                <DropUpload
                  kind="signage_media"
                  testid="sig-media-upload"
                  currentUrl={form.url}
                  onUploaded={(url, meta) => {
                    setForm((f) => ({
                      ...f,
                      url,
                      type: meta?.content_type?.startsWith("video/") ? "video" : "image",
                    }));
                  }}
                />
              </Field>
            </div>
            <DialogFooter>
              <Button disabled={!form.name || !form.url} onClick={create} data-testid="sig-media-create" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
        {list.length === 0 && (
          <div className="col-span-full rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
            No media yet.
          </div>
        )}
        {list.map((m) => (
          <div key={m.id} data-testid={`sig-media-${m.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden group">
            <div className="aspect-video bg-black">
              {m.type === "video" ? (
                <video src={m.url} muted className="w-full h-full object-cover" />
              ) : (
                <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-3">
              <div className="font-semibold truncate text-sm">{m.name}</div>
              <div className="flex items-center justify-between mt-2">
                <Badge variant="outline" className="border-white/10 text-xs capitalize">{m.type}</Badge>
                <Button onClick={() => remove(m.id)} data-testid={`sig-media-del-${m.id}`} variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400">
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- PLAYLISTS ---------- */
function PlaylistsTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", items: [] });

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/signage/playlists"), api.get("/signage/media")]);
    setList(p.data); setMedia(m.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/signage/playlists", form);
      toast.success("Playlist created");
      setOpen(false);
      setForm({ name: "", items: [] });
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    await api.delete(`/signage/playlists/${id}`);
    toast.success("Removed"); load(); onChanged();
  };

  const toggle = (mid) => {
    const idx = form.items.findIndex((i) => i.media_id === mid);
    if (idx >= 0) setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
    else setForm({ ...form, items: [...form.items, { media_id: mid, duration_sec: 10 }] });
  };

  return (
    <div className="space-y-5" data-testid="sig-playlists">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sig-add-playlist-btn" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">
              <Plus size={16} className="mr-2" /> New playlist
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create playlist</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Name">
                <Input data-testid="sig-playlist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" />
              </Field>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Select media</Label>
                <div className="mt-2 grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                  {media.map((m) => {
                    const on = form.items.some((i) => i.media_id === m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggle(m.id)}
                        data-testid={`sig-playlist-pick-${m.id}`}
                        className={`rounded-lg overflow-hidden border text-left ${on ? "border-[#D4B878] ring-2 ring-[#D4B878]" : "border-white/10"}`}
                      >
                        <div className="aspect-video bg-black">
                          {m.type === "video" ? (
                            <video src={m.url} muted className="w-full h-full object-cover" />
                          ) : (
                            <img src={m.url} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="p-2 text-xs truncate">{m.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="text-xs text-zinc-500">{form.items.length} selected · default 10s each</div>
            </div>
            <DialogFooter>
              <Button disabled={!form.name || !form.items.length} onClick={create} data-testid="sig-playlist-create" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {list.length === 0 && (
          <div className="col-span-full rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500">
            No playlists yet.
          </div>
        )}
        {list.map((p) => (
          <div key={p.id} data-testid={`sig-playlist-${p.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Playlist</div>
                <div className="font-bold text-lg">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{p.items?.length || 0} items</div>
              </div>
              <Button onClick={() => remove(p.id)} data-testid={`sig-playlist-del-${p.id}`} variant="ghost" size="sm" className="text-zinc-500 hover:text-red-400">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- SCHEDULES ---------- */
function SchedulesTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [screens, setScreens] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    screen_id: "",
    playlist_id: "",
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    start_hour: 8,
    end_hour: 22,
    active: true,
  });

  const load = async () => {
    const [a, s, p] = await Promise.all([
      api.get("/signage/schedules"),
      api.get("/signage/screens"),
      api.get("/signage/playlists"),
    ]);
    setList(a.data); setScreens(s.data); setPlaylists(p.data);
  };
  useEffect(() => { load(); }, []);

  const toggleDow = (d) => {
    const s = new Set(form.days_of_week);
    s.has(d) ? s.delete(d) : s.add(d);
    setForm({ ...form, days_of_week: Array.from(s).sort() });
  };

  const create = async () => {
    try {
      await api.post("/signage/schedules", form);
      toast.success("Schedule created");
      setOpen(false);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/signage/schedules/${id}`);
    toast.success("Removed"); load(); onChanged();
  };

  const screenName = (id) => screens.find((s) => s.id === id)?.name || "—";
  const playlistName = (id) => playlists.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="sig-schedules">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sig-add-sched-btn" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">
              <Plus size={16} className="mr-2" /> New schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle>Create schedule</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Screen">
                <Select value={form.screen_id} onValueChange={(v) => setForm({ ...form, screen_id: v })}>
                  <SelectTrigger data-testid="sig-sched-screen" className="bg-white/5 border-white/10"><SelectValue placeholder="Pick a screen" /></SelectTrigger>
                  <SelectContent>
                    {screens.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Playlist">
                <Select value={form.playlist_id} onValueChange={(v) => setForm({ ...form, playlist_id: v })}>
                  <SelectTrigger data-testid="sig-sched-playlist" className="bg-white/5 border-white/10"><SelectValue placeholder="Pick a playlist" /></SelectTrigger>
                  <SelectContent>
                    {playlists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Days</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DOW_LABELS.map((lbl, i) => {
                    const on = form.days_of_week.includes(i);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleDow(i)}
                        data-testid={`sig-sched-dow-${i}`}
                        className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${on ? "bg-[#D4B878] text-black border-[#D4B878]" : "bg-white/5 border-white/10 text-zinc-300"}`}
                      >{lbl}</button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start hour (0-23)">
                  <Input data-testid="sig-sched-start" type="number" min={0} max={23} value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: +e.target.value })} className="bg-white/5 border-white/10" />
                </Field>
                <Field label="End hour (1-24)">
                  <Input data-testid="sig-sched-end" type="number" min={1} max={24} value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: +e.target.value })} className="bg-white/5 border-white/10" />
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button disabled={!form.screen_id || !form.playlist_id} onClick={create} data-testid="sig-sched-create" className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
            <tr>
              <th className="text-left p-4">Screen</th>
              <th className="text-left p-4">Playlist</th>
              <th className="text-left p-4">Days</th>
              <th className="text-left p-4">Hours</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-zinc-500">No schedules yet.</td></tr>
            )}
            {list.map((sc) => (
              <tr key={sc.id} className="border-t border-white/5" data-testid={`sig-sched-${sc.id}`}>
                <td className="p-4 font-semibold">{screenName(sc.screen_id)}</td>
                <td className="p-4 text-zinc-300">{playlistName(sc.playlist_id)}</td>
                <td className="p-4 text-zinc-400 text-xs">
                  {sc.days_of_week.map((d) => DOW_LABELS[d]).join(" · ")}
                </td>
                <td className="p-4 text-[#E8D6A3] font-bold">
                  {sc.start_hour}:00 → {sc.end_hour}:00
                </td>
                <td className="p-4 text-right">
                  <Button onClick={() => remove(sc.id)} data-testid={`sig-sched-del-${sc.id}`} variant="ghost" size="sm" className="text-zinc-500 hover:text-red-400">
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/* ---------- ANALYTICS ---------- */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  const load = async (d = days) => {
    const { data } = await api.get(`/signage/analytics?days=${d}`);
    setData(data);
  };
  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (!data) {
    return <div className="text-zinc-500">Loading analytics…</div>;
  }

  const maxDay = Math.max(1, ...data.per_day.map((d) => d.plays));

  return (
    <div className="space-y-6" data-testid="sig-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-1">
            <Activity size={12} className="inline mr-1" /> Signage analytics
          </div>
          <div className="text-zinc-400 text-sm">Last {data.window_days} days of plays across every connected screen.</div>
        </div>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`sig-analytics-days-${d}`}
              className={`px-3 py-1 text-xs rounded-full ${days === d ? "bg-[#D4B878] text-black" : "text-zinc-400 hover:text-white"}`}
            >
              {d === 1 ? "24h" : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <AnalyticsKPI icon={<Film size={18} />} label="Total plays" value={data.total_plays.toLocaleString()} tone="gold" />
        <AnalyticsKPI icon={<Wifi size={18} />} label="Screens online" value={`${data.screens_online} / ${data.screens_total}`} />
        <AnalyticsKPI icon={<Tv size={18} />} label="Active screens" value={data.per_screen.length} />
        <AnalyticsKPI icon={<FolderOpen size={18} />} label="Media playing" value={data.top_media.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">Top media</div>
          {data.top_media.length === 0 && (
            <div className="text-zinc-600 text-sm py-8 text-center">No plays yet.</div>
          )}
          <div className="space-y-2">
            {data.top_media.map((m, i) => {
              const maxTop = data.top_media[0]?.plays || 1;
              const pct = (m.plays / maxTop) * 100;
              return (
                <div key={m.media_id} data-testid={`sig-top-media-${i}`} className="flex items-center gap-3">
                  <div className="text-[10px] text-zinc-500 w-4">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{m.name}</div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-gradient-to-r from-[#D4B878] to-[#E8D6A3]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[#E8D6A3]">{m.plays}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">Plays by screen</div>
          {data.per_screen.length === 0 && (
            <div className="text-zinc-600 text-sm py-8 text-center">No screen activity.</div>
          )}
          <div className="space-y-2">
            {data.per_screen.map((s) => {
              const maxScreen = data.per_screen[0]?.plays || 1;
              const pct = (s.plays / maxScreen) * 100;
              return (
                <div key={s.screen_id} data-testid={`sig-screen-stats-${s.screen_id}`} className="flex items-center gap-3">
                  <div className="text-sm font-semibold flex-1 truncate">{s.name}</div>
                  <div className="h-1.5 w-32 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-sm font-bold w-10 text-right">{s.plays}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">Daily plays</div>
        {data.per_day.length === 0 && (
          <div className="text-zinc-600 text-sm py-8 text-center">No plays in range.</div>
        )}
        <div className="flex items-end gap-1.5 h-32">
          {data.per_day.map((d) => {
            const h = Math.max(4, (d.plays / maxDay) * 100);
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`sig-day-${d.date}`}>
                <div className="w-full bg-gradient-to-t from-[#D4B878] to-[#E8D6A3] rounded-t" style={{ height: `${h}%` }} title={`${d.date}: ${d.plays}`} />
                <div className="text-[9px] text-zinc-500">{d.date.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AnalyticsKPI({ icon, label, value, tone }) {
  return (
    <div className={`rounded-2xl p-5 border ${tone === "gold" ? "border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-center gap-2 text-[#E8D6A3] mb-2">{icon}</div>
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
